import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearRecruitmentError,
  fetchAssessorCandidates,
  submitAssessorReview,
} from "../../store/slices/recruitmentSlice";
import { sendRecruitmentAssessorReviewNotification } from "../../emailjs/emailService";
import { fetchApplicationBySource } from "../../services/jobApplicationsService";
import "../admin/Recruitment/Recruitment.scss";

const AssessorRecruitment = () => {
  const dispatch = useDispatch();
  const { user, canAssessRecruitment } = useSelector((state) => state.auth);
  const { assessorCandidates, loadingAssessor, mutating, error } = useSelector(
    (state) => state.recruitment
  );
  const [activeCandidate, setActiveCandidate] = useState(null);
  const [applicationDetails, setApplicationDetails] = useState(null);
  const [applicationLoading, setApplicationLoading] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    score: 0,
    review: "Undecided",
    strengths: "",
    weaknesses: "",
  });

  useEffect(() => {
    if (user?.email) {
      dispatch(fetchAssessorCandidates(user.email));
    }
    return () => dispatch(clearRecruitmentError());
  }, [dispatch, user?.email]);

  useEffect(() => {
    if (!activeCandidate || !user?.email) return;
    const slot = activeCandidate.assessorOne === user.email ? "one" : "two";
    setReviewForm({
      score: slot === "one" ? Number(activeCandidate.oneScore || 0) : Number(activeCandidate.twoScore || 0),
      review: slot === "one" ? activeCandidate.assOneReview || "Undecided" : activeCandidate.assTwoReview || "Undecided",
      strengths: slot === "one" ? activeCandidate.oneSubmissionStrengths || "" : activeCandidate.twoSubmissionStrengths || "",
      weaknesses: slot === "one" ? activeCandidate.oneSubmissionWeaknesses || "" : activeCandidate.twoSubmissionWeaknesses || "",
    });
  }, [activeCandidate, user?.email]);

  useEffect(() => {
    const loadApplicationDetails = async () => {
      if (!activeCandidate?.sourceJobId || !activeCandidate?.sourceApplicationId) {
        setApplicationDetails(null);
        return;
      }
      try {
        setApplicationLoading(true);
        const details = await fetchApplicationBySource(
          activeCandidate.sourceJobId,
          activeCandidate.sourceApplicationId
        );
        setApplicationDetails(details);
      } catch (err) {
        console.error("Failed to load linked application details:", err);
        setApplicationDetails(null);
      } finally {
        setApplicationLoading(false);
      }
    };
    loadApplicationDetails();
  }, [activeCandidate?.sourceApplicationId, activeCandidate?.sourceJobId]);

  const candidateRows = useMemo(() => assessorCandidates || [], [assessorCandidates]);

  const getAssessorSlot = (candidate) => {
    if (!candidate || !user?.email) return null;
    if (candidate.assessorOne === user.email) return "one";
    if (candidate.assessorTwo === user.email) return "two";
    return null;
  };

  const handleSubmitReview = async () => {
    if (!activeCandidate) return;
    const assessorSlot = getAssessorSlot(activeCandidate);
    if (!assessorSlot) return;
    try {
      await dispatch(
        submitAssessorReview({
          candidateId: activeCandidate.id,
          assessorSlot,
          score: reviewForm.score,
          review: reviewForm.review,
          strengths: reviewForm.strengths,
          weaknesses: reviewForm.weaknesses,
        })
      ).unwrap();
      await sendRecruitmentAssessorReviewNotification(activeCandidate, {
        email: user?.email,
        displayName: user?.displayName || user?.email,
      }, {
        score: reviewForm.score,
        review: reviewForm.review,
        strengths: reviewForm.strengths,
        weaknesses: reviewForm.weaknesses,
      });
      setActiveCandidate(null);
      if (user?.email) {
        dispatch(fetchAssessorCandidates(user.email));
      }
    } catch (err) {
      console.error("Failed to submit assessor review:", err);
    }
  };

  if (!canAssessRecruitment) {
    return (
      <div className="recruitment-page">
        <div className="page-header">
          <h1>My Recruitment Reviews</h1>
          <p>You do not have any current assessor assignments.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="recruitment-page">
      <div className="page-header">
        <h1>My Recruitment Reviews</h1>
        <p>Review and score assigned candidates.</p>
      </div>

      {error && <div className="error-box">{error}</div>}

      <div className="table-wrap">
        <h3 className="section-title">Assigned Candidates</h3>
        <table className="recruitment-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Position</th>
              <th>Assessor Slot</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loadingAssessor ? (
              <tr>
                <td colSpan={6}>Loading assigned candidates...</td>
              </tr>
            ) : candidateRows.length === 0 ? (
              <tr>
                <td colSpan={6}>No assigned live candidates right now.</td>
              </tr>
            ) : (
              candidateRows.map((candidate) => {
                const slot = getAssessorSlot(candidate);
                return (
                  <tr key={candidate.id}>
                    <td>{candidate.name}</td>
                    <td>{candidate.email}</td>
                    <td>{candidate.positionTitle}</td>
                    <td>{slot === "one" ? "Assessor One" : "Assessor Two"}</td>
                    <td>{candidate.adminChecked ? "Locked by admin" : "Open"}</td>
                    <td>
                      <button type="button" onClick={() => setActiveCandidate(candidate)}>
                        Review
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {activeCandidate && (
        <div className="recruitment-modal-backdrop" onClick={() => setActiveCandidate(null)}>
          <div className="recruitment-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Assess Candidate - {activeCandidate.name}</h3>
            <div className="modal-grid">
              <p><strong>Position:</strong> {activeCandidate.positionTitle}</p>
              <p>
                <strong>Candidate CV:</strong>{" "}
                {activeCandidate.candidateCV ? (
                  <a href={activeCandidate.candidateCV} target="_blank" rel="noreferrer">
                    Open
                  </a>
                ) : (
                  "N/A"
                )}
              </p>
              <p>
                <strong>GitHub:</strong>{" "}
                {activeCandidate.gitHubProject ? (
                  <a href={activeCandidate.gitHubProject} target="_blank" rel="noreferrer">
                    Open
                  </a>
                ) : (
                  "N/A"
                )}
              </p>
            </div>
            <p>
              <strong>Admin initial remarks:</strong>{" "}
              {activeCandidate.adminInitialRemarks || "N/A"}
            </p>

            <div className="review-controls">
              <label>Application Profile (from Careers)</label>
              {applicationLoading ? (
                <p>Loading linked application details...</p>
              ) : applicationDetails ? (
                <div className="modal-grid">
                  <p><strong>Job:</strong> {applicationDetails.sourceJobTitle || "N/A"}</p>
                  <p><strong>Location:</strong> {applicationDetails.location || "N/A"}</p>
                  <p><strong>Current Position:</strong> {applicationDetails.currentPosition || "N/A"}</p>
                  <p><strong>Current Company:</strong> {applicationDetails.currentCompany || "N/A"}</p>
                  <p><strong>Experience:</strong> {applicationDetails.yearsOfExperience || "N/A"}</p>
                  <p><strong>Expected Salary:</strong> {applicationDetails.expectedSalary || "N/A"}</p>
                  <p>
                    <strong>LinkedIn:</strong>{" "}
                    {applicationDetails.linkedInUrl ? (
                      <a href={applicationDetails.linkedInUrl} target="_blank" rel="noreferrer">Open</a>
                    ) : "N/A"}
                  </p>
                  <p>
                    <strong>Portfolio:</strong>{" "}
                    {applicationDetails.portfolioUrl ? (
                      <a href={applicationDetails.portfolioUrl} target="_blank" rel="noreferrer">Open</a>
                    ) : "N/A"}
                  </p>
                  <p>
                    <strong>GitHub (application):</strong>{" "}
                    {applicationDetails.githubUrl ? (
                      <a href={applicationDetails.githubUrl} target="_blank" rel="noreferrer">Open</a>
                    ) : "N/A"}
                  </p>
                  <p>
                    <strong>Resume:</strong>{" "}
                    {applicationDetails.resumeUrl ? (
                      <a href={applicationDetails.resumeUrl} target="_blank" rel="noreferrer">Open</a>
                    ) : "N/A"}
                  </p>
                  <p>
                    <strong>Cover Letter File:</strong>{" "}
                    {applicationDetails.coverLetterUrl ? (
                      <a href={applicationDetails.coverLetterUrl} target="_blank" rel="noreferrer">Open</a>
                    ) : "N/A"}
                  </p>
                </div>
              ) : (
                <p>No linked careers application data found for this candidate.</p>
              )}
              {!applicationLoading && applicationDetails && (
                <div className="longtext-grid">
                  <div className="longtext-card">
                    <p className="longtext-title">Cover Letter Text</p>
                    <p className="longtext-content">
                      {applicationDetails.coverLetter || "N/A"}
                    </p>
                  </div>
                  <div className="longtext-card">
                    <p className="longtext-title">Additional Info</p>
                    <p className="longtext-content">
                      {applicationDetails.additionalInfo || "N/A"}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="review-controls">
              <label>Score (1-10)</label>
              <div className="score-range-wrap">
                <input
                  className="score-range"
                  type="range"
                  min={1}
                  max={10}
                  step={1}
                  value={Math.min(Math.max(Number(reviewForm.score || 1), 1), 10)}
                  onChange={(e) =>
                    setReviewForm((prev) => ({
                      ...prev,
                      score: Number(e.target.value || 1),
                    }))
                  }
                />
                <div className="score-range-meta">
                  <div className="score-range-ticks">
                    {Array.from({ length: 10 }, (_, i) => (
                      <span key={`tick-${i + 1}`}>{i + 1}</span>
                    ))}
                  </div>
                  <span className="score-value-badge">
                    Score: {Math.min(Math.max(Number(reviewForm.score || 1), 1), 10)}
                  </span>
                </div>
              </div>
              <label>Review</label>
              <select
                className="form-input"
                value={reviewForm.review}
                onChange={(e) =>
                  setReviewForm((prev) => ({ ...prev, review: e.target.value }))
                }
              >
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
                <option value="Undecided">Undecided</option>
              </select>
              <label>Submission strengths</label>
              <textarea
                className="form-input"
                rows={3}
                value={reviewForm.strengths}
                onChange={(e) =>
                  setReviewForm((prev) => ({ ...prev, strengths: e.target.value }))
                }
              />
              <label>Submission weaknesses</label>
              <textarea
                className="form-input"
                rows={3}
                value={reviewForm.weaknesses}
                onChange={(e) =>
                  setReviewForm((prev) => ({ ...prev, weaknesses: e.target.value }))
                }
              />
            </div>

            <div className="modal-actions">
              <button
                type="button"
                disabled={mutating || activeCandidate.adminChecked}
                onClick={handleSubmitReview}
              >
                {activeCandidate.adminChecked ? "Locked by Admin" : "Submit Review"}
              </button>
              <button type="button" onClick={() => setActiveCandidate(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessorRecruitment;
